/**
 * iter 72 (Phase D) — Org Unification Engine — Review Merge drawer.
 *
 * Opens with the preflight `preview` payload. Walks Super Admin A through:
 *   1. Migration summary (counts, blockers, source/target).
 *   2. User collision review — for each colliding user choose
 *      'merge_into' (fold into A's existing user) or 'suffix' (rename email).
 *   3. Role mapping — auto-match by name, dropdowns for the rest.
 *   4. Department mapping — same pattern.
 *   5. Submit for Platform Approval.
 *
 * Visual: single tall drawer with section accordions (kept simple — a wizard
 * felt heavier than the data justifies; everything fits on one scroll).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, X, WarningCircle, Users, ShieldCheck, Buildings } from "@phosphor-icons/react";
import { api, formatErr } from "../lib/api";
import { useBrand } from "./AppShell";
import { Drawer } from "./UI";

/** Build a default decisions array from preview.preflight.user_collisions. */
function defaultUserDecisions(preview) {
  // Default action = 'merge_into' (matches the existing target user) — the
  // most common Cleanup-mode intent. Super Admin can flip to 'suffix' per row.
  return (preview?.preflight?.user_collisions || []).map(c => ({
    source_user_id: c.source_user_id,
    source_email: c.source_email,
    source_name: c.source_name,
    target_user_id: c.target_user_id,
    target_name: c.target_name,
    action: "merge_into",
    suffix_email: "",
  }));
}

/** Auto-map role/dept by exact lowercase name match. */
function autoMapByName(sourceList, targetList) {
  const byName = new Map(targetList.map(t => [t.name.toLowerCase(), t.id]));
  return sourceList.map(s => ({
    source_id: s.id,
    source_name: s.name,
    source_count: s.user_count,
    target_id: byName.get(s.name.toLowerCase()) || "",
  }));
}

export default function ReviewMergeDrawer({ preview, token, onClose, onSubmitted }) {
  const { platformName } = useBrand();
  const [decisions, setDecisions] = useState(() => defaultUserDecisions(preview));
  const [roles, setRoles] = useState(() =>
    autoMapByName(preview?.preflight?.role_summary || [], preview?.target_roles || [])
  );
  const [depts, setDepts] = useState(() =>
    autoMapByName(preview?.preflight?.dept_summary || [], preview?.target_departments || [])
  );
  const [busy, setBusy] = useState(false);

  const blockers = preview?.preflight?.blockers || [];
  const warnings = preview?.preflight?.warnings || [];
  const totals = preview?.preflight?.totals || {};
  const mode = preview?.key?.mode;
  const sourceName = preview?.key?.source_org?.name || "the source org";
  const canProceed = blockers.length === 0;

  const setDecision = (i, patch) =>
    setDecisions(prev => prev.map((d, idx) => idx === i ? { ...d, ...patch } : d));

  const submit = async () => {
    // Validate decisions
    for (const d of decisions) {
      if (d.action === "suffix" && !d.suffix_email.trim()) {
        toast.error(`Suffix email missing for ${d.source_email}`);
        return;
      }
    }
    if (!confirm(
      `Submit this merge for platform approval?\n\n` +
      `Source: ${sourceName}\nMode: ${mode.toUpperCase()}\n\n` +
      `A ${platformName} platform reviewer will verify before any data moves. ` +
      `This operation is FINAL once executed.`
    )) return;
    setBusy(true);
    try {
      await api.post("/unify/jobs", {
        key_token: token,
        user_decisions: decisions.map(d => ({
          source_user_id: d.source_user_id,
          action: d.action,
          target_user_id: d.action === "merge_into" ? d.target_user_id : null,
          suffix_email: d.action === "suffix" ? d.suffix_email.trim() : null,
        })),
        role_mappings: roles.map(r => ({
          source_role_id: r.source_id,
          target_role_id: r.target_id || null,
        })),
        dept_mappings: depts.map(d => ({
          source_department_id: d.source_id,
          target_department_id: d.target_id || null,
        })),
      });
      toast.success("Submitted for platform approval.");
      onSubmitted?.();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Drawer
      open={true}
      onClose={onClose}
      title={`Review merge — ${sourceName} → this org`}
      size="wide"
      testid="review-merge-drawer"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy} data-testid="review-merge-cancel-btn">CANCEL</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={busy || !canProceed}
            data-testid="review-merge-submit-btn"
          >
            <CheckCircle size={14} /> SUBMIT FOR PLATFORM APPROVAL
          </button>
        </>
      }
    >
      {/* ============================== SUMMARY ============================== */}
      <section className="surface p-4" data-testid="review-merge-summary">
        <div className="label-xs mb-2">MIGRATION SUMMARY</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div><span className="text-muted-foreground">Source org:</span> <strong>{sourceName}</strong></div>
          <div><span className="text-muted-foreground">Mode:</span> <strong className="uppercase">{mode}</strong></div>
        </div>
        <div className="grid grid-cols-3 gap-2 text-xs mt-3" data-testid="review-merge-totals">
          {Object.entries(totals).map(([k, v]) => (
            <div key={k} className="surface p-2 bg-muted/40">
              <div className="text-muted-foreground">{k}</div>
              <div className="text-lg mono">{v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============================== BLOCKERS ============================== */}
      {blockers.length > 0 && (
        <section className="surface p-4 border-destructive/40 mt-3" data-testid="review-merge-blockers">
          <div className="label-xs text-destructive mb-2 flex items-center gap-2">
            <WarningCircle size={14} weight="duotone" /> PRE-FLIGHT BLOCKERS — RESOLVE BEFORE PROCEEDING
          </div>
          <ul className="space-y-1 text-sm">
            {blockers.map((b, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`review-merge-blocker-${b.code}`}>
                <X size={14} className="text-destructive mt-1 shrink-0" weight="bold" />
                <span>{b.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ================== iter 73.12 — PRE-FLIGHT WARNINGS ================== */}
      {/* Non-blocking; the operator can still submit, but should be aware.  */}
      {warnings.length > 0 && (
        <section className="surface p-4 border-amber-500/40 bg-amber-500/5 mt-3" data-testid="review-merge-warnings">
          <div className="label-xs text-amber-700 dark:text-amber-300 mb-2 flex items-center gap-2">
            <WarningCircle size={14} weight="duotone" /> PRE-FLIGHT WARNINGS
          </div>
          <ul className="space-y-1 text-sm">
            {warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-2" data-testid={`review-merge-warning-${w.code}`}>
                <WarningCircle size={14} className="text-amber-600 mt-1 shrink-0" weight="bold" />
                <span>{w.message}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ============================== USERS ============================== */}
      <section className="surface p-4 mt-3" data-testid="review-merge-users">
        <div className="label-xs mb-2 flex items-center gap-2">
          <Users size={14} /> USER COLLISIONS ({decisions.length})
        </div>
        {decisions.length === 0 ? (
          <div className="text-xs text-muted-foreground">No email collisions detected — every source user will be ported as-is.</div>
        ) : (
          <table className="dense">
            <thead><tr>
              <th>SOURCE USER</th><th>TARGET MATCH</th><th>ACTION</th><th>SUFFIX EMAIL</th>
            </tr></thead>
            <tbody>
              {decisions.map((d, i) => (
                <tr key={d.source_user_id} data-testid={`review-merge-user-row-${i}`}>
                  <td><div>{d.source_name}</div><div className="text-xs text-muted-foreground">{d.source_email}</div></td>
                  <td>{d.target_name}</td>
                  <td>
                    <select
                      className="input"
                      value={d.action}
                      onChange={e => setDecision(i, { action: e.target.value })}
                      data-testid={`review-merge-user-action-${i}`}
                    >
                      <option value="merge_into">Merge into existing</option>
                      <option value="suffix">Keep separate (rename email)</option>
                    </select>
                  </td>
                  <td>
                    {d.action === "suffix" && (
                      <input
                        className="input mono text-xs"
                        value={d.suffix_email}
                        onChange={e => setDecision(i, { suffix_email: e.target.value })}
                        placeholder={d.source_email.replace("@", "+merged@")}
                        data-testid={`review-merge-user-suffix-${i}`}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ============================== ROLES ============================== */}
      <section className="surface p-4 mt-3" data-testid="review-merge-roles">
        <div className="label-xs mb-2 flex items-center gap-2">
          <ShieldCheck size={14} /> ROLE MAPPING ({roles.length})
        </div>
        {roles.length === 0 ? (
          <div className="text-xs text-muted-foreground">No source-org roles to map.</div>
        ) : (
          <table className="dense">
            <thead><tr><th>SOURCE ROLE</th><th>USERS</th><th>TARGET ROLE (or carry over if blank)</th></tr></thead>
            <tbody>
              {roles.map((r, i) => (
                <tr key={r.source_id} data-testid={`review-merge-role-row-${i}`}>
                  <td><strong>{r.source_name}</strong></td>
                  <td><span className="text-muted-foreground">{r.source_count}</span></td>
                  <td>
                    <select
                      className="input"
                      value={r.target_id}
                      onChange={e =>
                        setRoles(prev => prev.map((x, idx) => idx === i ? { ...x, target_id: e.target.value } : x))
                      }
                      data-testid={`review-merge-role-select-${i}`}
                    >
                      <option value="">— Carry over (create on target with same name)</option>
                      {(preview?.target_roles || []).map(t =>
                        <option key={t.id} value={t.id}>{t.name}</option>
                      )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ============================== DEPTS ============================== */}
      <section className="surface p-4 mt-3" data-testid="review-merge-depts">
        <div className="label-xs mb-2 flex items-center gap-2">
          <Buildings size={14} /> DEPARTMENT MAPPING ({depts.length})
        </div>
        {depts.length === 0 ? (
          <div className="text-xs text-muted-foreground">No source-org departments to map.</div>
        ) : (
          <table className="dense">
            <thead><tr><th>SOURCE DEPT</th><th>USERS</th><th>TARGET DEPT (or carry over if blank)</th></tr></thead>
            <tbody>
              {depts.map((d, i) => (
                <tr key={d.source_id} data-testid={`review-merge-dept-row-${i}`}>
                  <td><strong>{d.source_name}</strong></td>
                  <td><span className="text-muted-foreground">{d.source_count}</span></td>
                  <td>
                    <select
                      className="input"
                      value={d.target_id}
                      onChange={e =>
                        setDepts(prev => prev.map((x, idx) => idx === i ? { ...x, target_id: e.target.value } : x))
                      }
                      data-testid={`review-merge-dept-select-${i}`}
                    >
                      <option value="">— Carry over (create on target with same name)</option>
                      {(preview?.target_departments || []).map(t =>
                        <option key={t.id} value={t.id}>{t.name}</option>
                      )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </Drawer>
  );
}
